import React, { useMemo, useState } from 'react';
import { Paper, Grid, Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface DeliveryEntry {
  total: string | number;
}

/** month key format: "Aug-25", "Jan-26", ... */
export type StatusMatrixData = Record<string, Record<string, DeliveryEntry>>;

interface MonthColumn {
  key: string;
  label: string; // French display label
}

interface StatusRowDef {
  key: string;
  label: string;
  tooltip: string;
  order: number; // canonical order, -1 when unknown
}

type SortKey = 'status' | 'total' | string; // string => a month key
type SortDirection = 'asc' | 'desc';
interface SortState {
  key: SortKey | null;
  direction: SortDirection;
}

interface StatusMatrixProps {
  /** Aggregated source data from the API. Defaults to the demo dataset below. */
  data?: StatusMatrixData;
  symbol?: string;
  /** Status rendered with the cyan accent (default "Purchased"). */
  highlightedStatus?: string;
  /** Decimals shown in cells (design uses 0). */
  decimals?: number;
  /** Fill missing months between the first and last so the axis is continuous. */
  fillGaps?: boolean;
  /** Always render every status in KNOWN_STATUS_ORDER, even when absent from `data`. */
  showAllStatuses?: boolean;
}

/* ------------------------- Month metadata ------------------------- */

const MONTH_EN: Record<string, { idx: number; en: string }> = {
  Jan: { idx: 0, en: 'Jan' },
  Feb: { idx: 1, en: 'Feb' },
  Mar: { idx: 2, en: 'Mar' },
  Apr: { idx: 3, en: 'Apr' },
  May: { idx: 4, en: 'May' },
  Jun: { idx: 5, en: 'Jun' },
  Jul: { idx: 6, en: 'Jul' },
  Aug: { idx: 7, en: 'Aug' },
  Sep: { idx: 8, en: 'Sep' },
  Oct: { idx: 9, en: 'Oct' },
  Nov: { idx: 10, en: 'Nov' },
  Dec: { idx: 11, en: 'Dec' },
};
const IDX_TO_ABBR: string[] = Object.keys(MONTH_EN).reduce<string[]>((acc, abbr) => {
  acc[MONTH_EN[abbr].idx] = abbr;
  return acc;
}, []);

function parseMonthKey(key: string): { ordinal: number; label: string } | null {
  const [mon, yy] = key.split('-');
  const meta = MONTH_EN[mon];
  if (!meta || yy === undefined) return null;
  const year = 2000 + parseInt(yy, 10);
  return { ordinal: year * 12 + meta.idx, label: `${meta.en}-${yy}` };
}

function ordinalToColumn(ordinal: number): MonthColumn {
  const year = Math.floor(ordinal / 12);
  const idx = ((ordinal % 12) + 12) % 12;
  const yy = String(year).slice(-2);
  const key = `${IDX_TO_ABBR[idx]}-${yy}`;
  return { key, label: `${MONTH_EN[IDX_TO_ABBR[idx]].en}-${yy}` };
}

const KNOWN_STATUS_ORDER: string[] = [
  'Purchased',
  'Baseline',
  'Cancelled',
  'Added',
  'Postponed OUT',
  'Postponed IN',
  'Forecasted',
  'Submitted',
  'Validated DM',
  'Refused Client',
  'Accepted Client',
  'Invoiced',
];
// status values come straight from the API; map them to their i18n keys
const STATUS_LABEL_KEYS: Record<string, string> = {
  Purchased: 'Purchased',
  Baseline: 'Baseline',
  Cancelled: 'Cancelled',
  Added: 'Added',
  'Postponed OUT': 'Postponed_Out',
  'Postponed IN': 'Postponed_In',
  Forecasted: 'Forecasted',
  Submitted: 'Submitted',
  'Validated DM': 'Validated_Dm',
  'Refused Client': 'Refused_Client',
  'Accepted Client': 'Accepted_Client',
  Invoiced: 'Invoiced',
};

const STATUS_TOOLTIPS: Record<string, string> = {
  Purchased: 'Amount of the Purchase Order coming from sales department',
  Baseline: 'Amount of the Workunits initially loaded in e-Delivery during the Quote creation.',
  Cancelled: 'Amount of Cancelled Workunits.',
  Added: 'Amount of Added workunits.',
  'Postponed OUT': 'Amount of workunits postponed to another month.',
  'Postponed IN': 'Amount of Workunits rescheduled to this month from another month.',
  Forecasted: 'Forecast = Baseline - Cancelled + Added - Postponed OUT + Postponed IN.',
  Submitted: 'Amount of workunits submitted by the consultant.',
  'Validated DM': 'Amount of workunits validated by the DM.',
  'Refused Client': 'Amount of workunits rejected by the client.',
  'Accepted Client': 'Amount of workunits accepted by the client.',
  Invoiced: 'Actual amount invoiced to the customer.',
};

/* ----------------------------- Palette ---------------------------- */
const C = {
  bg: '#2C2B64',
  row: '#2C2B64',
  rowAlt: '#4b4b8c',
  rowHi: '#4b4b8c',
  line: 'rgba(255,255,255,0.07)',
  text: '#eef0ff',
  muted: '#b9bbe0',
};

/* Column widths shared by header + body so everything stays aligned. */
const COL_W = { status: 220, month: 126, total: 120 };
const colSx: Record<'status' | 'month' | 'total', SxProps<Theme>> = {
  status: { flex: `0 0 ${COL_W.status}px`, minWidth: COL_W.status, textAlign: 'left', whiteSpace: 'nowrap' },
  month: { flex: '1 1 0px', minWidth: COL_W.month, textAlign: 'right', whiteSpace: 'nowrap' },
  total: { flex: `0 0 ${COL_W.total}px`, minWidth: COL_W.total, textAlign: 'right', whiteSpace: 'nowrap' },
};

/* ---------------------------- Component --------------------------- */

interface AggRow {
  byMonth: Record<string, number | null>;
  total: number;
}

const StatusMatrixFinacialKpi: React.FC<StatusMatrixProps> = ({
  data = [],
  symbol = '€',
  highlightedStatus = '',
  decimals = 0,
  fillGaps = false,
  showAllStatuses = false,
}) => {
  const [t] = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: null, direction: 'asc' });

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [decimals]
  );

  /* ---- Months derived from the response ---- */
  const months: MonthColumn[] = useMemo(() => {
    const ordinals: number[] = [];
    Object.values(data).forEach((byMonth) => {
      Object.keys(byMonth).forEach((mk) => {
        const p = parseMonthKey(mk);
        if (p) ordinals.push(p.ordinal);
      });
    });
    if (ordinals.length === 0) return [];
    const uniqueSorted = Array.from(new Set(ordinals)).sort((a, b) => a - b);
    if (!fillGaps) return uniqueSorted.map(ordinalToColumn);
    const start = uniqueSorted[0];
    const end = uniqueSorted[uniqueSorted.length - 1];
    return Array.from({ length: end - start + 1 }, (_, i) => ordinalToColumn(start + i));
  }, [data, fillGaps]);

  /* ---- Status rows derived from the response ---- */
  const statusRows: StatusRowDef[] = useMemo(() => {
    const keys = new Set(showAllStatuses ? [...KNOWN_STATUS_ORDER, ...Object.keys(data)] : Object.keys(data));
    return Array.from(keys)
      .map<StatusRowDef>((key) => ({
        key,
        label: key,
        tooltip: STATUS_TOOLTIPS[key] ?? '',
        order: KNOWN_STATUS_ORDER.indexOf(key),
      }))
      .sort((a, b) => {
        const ao = a.order === -1 ? Number.MAX_SAFE_INTEGER : a.order;
        const bo = b.order === -1 ? Number.MAX_SAFE_INTEGER : b.order;
        return ao !== bo ? ao - bo : a.label.localeCompare(b.label);
      });
  }, [data, showAllStatuses]);

  /* ---- Aggregation ---- */
  const agg: Record<string, AggRow> = useMemo(
    () =>
      Object.fromEntries(
        statusRows.map((row) => {
          const src = (data as StatusMatrixData)[row.key] ?? {};
          // Cancelled amounts always subtract from the total, so force the minus sign
          // regardless of the raw sign the API sends (mirrors PopriceDashboard's Cancelled column).
          const monthSums = months.map((m) => {
            const entry = src[m.key];
            const raw = entry !== undefined ? Number(entry.total) || 0 : null;
            const sum = raw !== null && row.key === 'Cancelled' ? -Math.abs(raw) : raw;
            return [m.key, sum] as const;
          });
          const byMonth = Object.fromEntries(monthSums) as Record<string, number | null>;
          const total = monthSums.reduce((acc, [, v]) => acc + (v ?? 0), 0);
          return [row.key, { byMonth, total }] as const;
        })
      ),
    [data, months, statusRows]
  );

  /* ---- Sorting ---- */
  const orderedRows = useMemo(() => {
    if (sort.key === null) return statusRows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const valueOf = (r: StatusRowDef): number | string | null => {
      if (sort.key === 'status') return r.label.toLowerCase();
      if (sort.key === 'total') return agg[r.key].total;
      return agg[r.key].byMonth[sort.key as string];
    };
    return [...statusRows].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1; // empties sink to the bottom
      if (vb === null) return -1;
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return ((va as number) - (vb as number)) * dir;
    });
  }, [sort, statusRows, agg]);

  const toggleSort = (key: SortKey): void =>
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });

  const Arrow: React.FC<{ k: SortKey }> = ({ k }) => {
    const active = sort.key === k;
    const activeGlyph = sort.direction === 'asc' ? '▲' : '▼';
    const glyph = active ? activeGlyph : '↕';
    return (
      <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: C.text }}>
        {glyph}
      </Box>
    );
  };

  const minWidth = COL_W.status + months.length * COL_W.month + COL_W.total;

  /* Reusable header cell */
  const HeaderCell: React.FC<{
    sortKey: SortKey | false;
    variant: 'status' | 'month' | 'total';
    children: React.ReactNode;
    isSticky?: boolean;
  }> = ({ sortKey, variant, children, isSticky = false }) => (
    <Grid
      item
      onClick={() => sortKey && toggleSort(sortKey)}
      sx={{
        ...colSx[variant],
        px: 1.5,
        py: 1,
        fontSize: 14,
        userSelect: 'none',
        whiteSpace: 'nowrap',
        ...(isSticky && {
          position: 'sticky',
          whiteSpace: 'nowrap',
          left: 0,
          zIndex: 2,
          bgcolor: C.bg,
        }),
        ...(sortKey && {
          '&:hover': { bgcolor: isSticky ? C.bg : 'rgba(255,255,255,0.05)' },
        }),
      }}
    >
      {children}
      {sortKey && <Arrow k={sortKey} />}
    </Grid>
  );

  return (
    <Paper
      elevation={6}
      sx={{
        bgcolor: C.bg,
        color: C.text,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
        <Box sx={{ minWidth }}>
          {/* Header */}
          <Grid container wrap="nowrap" sx={{ borderBottom: `3px solid #206AC9`, fontSize: 14, fontWeight: 600 }}>
            <HeaderCell sortKey="status" variant="status" isSticky>
              {t('Status')}
            </HeaderCell>
            {months.map((m) => (
              <HeaderCell key={m.key} sortKey={false} variant="month">
                {m.label}
              </HeaderCell>
            ))}
            <HeaderCell sortKey="total" variant="total">
              {t('Total')}
            </HeaderCell>
          </Grid>
          {/* Body */}
          {orderedRows.length === 0 && (
            <Box sx={{ px: 2, py: 3, color: C.muted, fontSize: 14 }}>No data to display.</Box>
          )}
          {orderedRows.map((row, i) => {
            const r = agg[row.key];
            const isHi = row.key === highlightedStatus;
            const stripe = i % 2 === 0 ? C.row : C.rowAlt;
            const base = isHi ? C.rowHi : stripe;
            return (
              <Grid
                container
                key={row.key}
                wrap="nowrap"
                sx={{
                  bgcolor: base,
                  fontSize: 14,
                  borderBottom: `1px solid ${C.line}`,
                  transition: 'background-color .12s',
                }}
              >
                {/* Status cell + tooltip */}
                <Grid
                  item
                  sx={{
                    ...colSx.status,
                    px: 1.5,
                    py: 0.9,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: base,
                    borderRight: `3px solid #206AC9`,
                  }}
                >
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: 13,
                          padding: '4px 6px',
                          borderRadius: '6px',
                        },
                      },
                    }}
                    title={row.tooltip}
                    arrow
                    placement="bottom-end"
                    disableHoverListener={!row.tooltip}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {t(STATUS_LABEL_KEYS[row.label] ?? row.label)}
                    </Box>
                  </Tooltip>
                </Grid>
                {/* Month cells */}
                {months.map((m) => {
                  const v = r.byMonth[m.key];
                  return (
                    <Grid item key={m.key} sx={{ ...colSx.month, px: 1.5, py: 0.9 }}>
                      {v === null ? 0 : formatter.format(v)} {symbol}
                    </Grid>
                  );
                })}
                {/* Total cell */}
                <Grid item sx={{ ...colSx.total, px: 1.5, py: 0.9 }}>
                  {formatter.format(r.total)} {symbol}
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default StatusMatrixFinacialKpi;
